import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchBar } from '@shared/components/SearchBar';
import { FilterBar } from '@shared/components/FilterBar';
import { FilterPopup } from '@shared/components/FilterPopup';
import { LottieAnimation } from '@shared/components/LottieAnimation';
import noDataAnimation from '@/assets/lottie/noDataAnimation.json';
import noResultsAnimation from '@/assets/lottie/noResultsAnimation.json';
import { SUBMITTED_FORM_LABELS } from '@epics/taskFormManagement/constants/labels';
import { useSubmittedFormList } from './useSubmittedFormList';
import { SubmittedFormCard } from './SubmittedFormCard';
import type { SubmittedForm } from '@epics/taskFormManagement/types';

export default function SubmittedFormList() {
  const navigate = useNavigate();
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const {
    forms,
    loading,
    hasNextPage,
    fetchError,
    isNoFilterFetch,
    hasActiveSearch,
    hasActiveFilter,
    search,
    setSearch,
    dateRange,
    setDateRange,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    sentinelRef,
    scrollContainerRef,
  } = useSubmittedFormList();

  const filterCount =
    (appliedFilters.dateRange.startDate ? 1 : 0) +
    (appliedFilters.dateRange.endDate ? 1 : 0);

  const handleApplyAndClose = () => {
    handleApplyFilters();
    setIsFilterOpen(false);
  };

  const handleClearAndClose = () => {
    handleClearFilters();
    setIsFilterOpen(false);
  };

  const handleView = (form: SubmittedForm) => {
    navigate(`Podgląd/${form.sr_id}`);
  };

  const showSearchAndFilter =
    !isNoFilterFetch || forms.length > 0 || hasActiveSearch || hasActiveFilter;

  const noResultsSubtitle =
    hasActiveSearch && hasActiveFilter
      ? SUBMITTED_FORM_LABELS.NO_RESULTS_SEARCH_AND_FILTER
      : hasActiveSearch
        ? SUBMITTED_FORM_LABELS.NO_RESULTS_SEARCH_ONLY
        : SUBMITTED_FORM_LABELS.NO_RESULTS_FILTER_ONLY;

  return (
    <div className="w-full h-full flex flex-col p-4 sm:p-6 bg-white shadow-[0px_2px_4px_rgba(0,0,0,0.08)] rounded gap-4 overflow-hidden">
      {/* Page title */}
      <h1 className="text-[22px] font-normal text-[#181A1B] leading-7 shrink-0">
        {SUBMITTED_FORM_LABELS.LIST_TITLE}
      </h1>

      {/* Search + Filter */}
      {showSearchAndFilter && (
        <div className="flex items-center gap-3 relative shrink-0">
          <div className="flex-1">
            <SearchBar
              value={search}
              onChange={setSearch}
              placeholder={SUBMITTED_FORM_LABELS.SEARCH_PLACEHOLDER}
            />
          </div>
          <FilterBar
            onClick={() => setIsFilterOpen((prev) => !prev)}
            label={SUBMITTED_FORM_LABELS.FILTER_BUTTON}
            filterCount={filterCount}
          />
          <FilterPopup
            isOpen={isFilterOpen}
            onClose={() => setIsFilterOpen(false)}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            onApply={handleApplyAndClose}
            onClear={handleClearAndClose}
          />
        </div>
      )}

      {/* Scrollable content area */}
      <div
        ref={scrollContainerRef}
        className="flex-1 min-h-0 overflow-y-auto custom-scrollbar"
      >
        {/* Loading skeleton — initial load */}
        {loading && forms.length === 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="bg-white border border-[#DCDDE0] rounded-[12px] p-5 flex flex-col gap-4"
              >
                <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
                <div className="flex flex-col gap-2">
                  <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2" />
                  <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2" />
                  <div className="h-3 bg-gray-200 rounded animate-pulse w-2/5" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Cards grid */}
        {forms.length > 0 && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {forms.map((form) => (
                <SubmittedFormCard
                  key={form.id}
                  form={form}
                  onView={handleView}
                />
              ))}
            </div>

            {/* Infinite scroll sentinel */}
            {hasNextPage && <div ref={sentinelRef} className="h-1 shrink-0" />}

            {/* Load-more skeleton */}
            {loading && forms.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                {Array.from({ length: 2 }).map((_, i) => (
                  <div
                    key={i}
                    className="bg-white border border-[#DCDDE0] rounded-[12px] p-5 flex flex-col gap-4"
                  >
                    <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
                    <div className="h-5 bg-gray-200 rounded-full animate-pulse w-24" />
                    <div className="flex flex-col gap-2">
                      <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2" />
                      <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2" />
                      <div className="h-3 bg-gray-200 rounded animate-pulse w-2/5" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Fetch error */}
        {!loading && fetchError && (
          <p className="text-center text-sm text-error py-8">
            {SUBMITTED_FORM_LABELS.FETCH_ERROR}
          </p>
        )}

        {/* Empty state — no forms at all */}
        {!loading && !fetchError && forms.length === 0 && isNoFilterFetch && (
          <div className="flex flex-col items-center gap-6 py-12">
            <LottieAnimation
              animationData={noDataAnimation}
              width={240}
              aspectRatio="240 / 194"
            />
            <div className="flex flex-col items-center gap-1">
              <p className="text-[15px] font-medium text-[#474A4C] text-center">
                {SUBMITTED_FORM_LABELS.EMPTY_TITLE}
              </p>
              <p className="text-sm text-[#6B6E72] text-center">
                {SUBMITTED_FORM_LABELS.EMPTY_SUBTITLE}
              </p>
            </div>
          </div>
        )}

        {/* No results — search / filter active but no matches */}
        {!loading && !fetchError && forms.length === 0 && !isNoFilterFetch && (
          <div className="flex flex-col items-center gap-6 py-12">
            <LottieAnimation
              animationData={noResultsAnimation}
              width={240}
              aspectRatio="240 / 194"
            />
            <div className="flex flex-col items-center gap-1">
              <p className="text-[15px] font-medium text-[#474A4C] text-center">
                {SUBMITTED_FORM_LABELS.NO_RESULTS_TITLE}
              </p>
              <p className="text-sm text-[#6B6E72] text-center max-w-[280px]">
                {noResultsSubtitle}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
